import React, { useState } from 'react';
import { Button } from './button';
import { Dialog, DialogTitle, DialogDescription } from './dialog';
import { Input } from './input';
import { Text } from './text';

interface ContractSigningModalProps {
  isOpen: boolean;
  onClose: () => void;
  contractText: string | null;
  isLoadingContract: boolean;
  isSigningContract: boolean;
  isDownloadingContract: boolean;
  onDownloadContract: () => void;
  onSignContract: (firstName: string, lastName: string) => void;
}

export function ContractSigningModal({
  isOpen,
  onClose,
  contractText,
  isLoadingContract,
  isSigningContract,
  isDownloadingContract,
  onDownloadContract,
  onSignContract,
}: ContractSigningModalProps) {
  const [signedFirstName, setSignedFirstName] = useState('');
  const [signedLastName, setSignedLastName] = useState('');

  const handleSign = () => {
    if (signedFirstName.trim() && signedLastName.trim()) {
      onSignContract(signedFirstName.trim(), signedLastName.trim());
    }
  };

  const handleClose = () => {
    setSignedFirstName('');
    setSignedLastName('');
    onClose();
  };

  return (
    // TODO DARK MODE STYLES: Dialog component sets bg color at zinc-900, we should modify this to match our
    // styles once we land on them
    <Dialog open={isOpen} onClose={handleClose} size="4xl">
      <div className="tw:flex tw:flex-col tw:h-full tw:max-h-[90vh]">
        {/* Header */}
        <div className="tw:flex tw:items-start tw:justify-between tw:mb-2">
          <div>
            <DialogTitle>Professional Services Agreement</DialogTitle>
            <DialogDescription className="tw:mt-2">
              {isDownloadingContract ? (
                <div className="tw:flex tw:items-center tw:gap-2">
                  <div className="tw:animate-spin tw:rounded-full tw:h-4 tw:w-4 tw:border-2 tw:border-zinc-300 tw:border-t-zinc-600" />
                  <Text>Downloading...</Text>
                </div>
              ) : (
                <Text>
                  Please review and sign at the bottom of the page.{' '}
                  <button
                    onClick={onDownloadContract}
                    className="tw:text-brand-green-0 tw:underline tw:hover:text-brand-green-0/90 tw:focus:outline-none tw:focus:ring-2 tw:focus:ring-brand-green-0 tw:focus:ring-offset-2 tw:cursor-pointer"
                    disabled={isDownloadingContract}
                  >
                    Click here to download
                  </button>{' '}
                  and review.
                </Text>
              )}
            </DialogDescription>
          </div>
          <button
            onClick={handleClose}
            className="tw:text-zinc-400 tw:hover:text-zinc-600 tw:focus:outline-none tw:focus:ring-2 tw:focus:ring-brand-green-0 tw:focus:ring-offset-2 tw:rounded"
            disabled={isSigningContract}
          >
            <i className="fa-solid fa-xmark tw:text-lg" />
          </button>
        </div>

        {/* Content */}
        <div className="tw:flex-1 tw:overflow-y-auto tw:mb-6">
          {isLoadingContract ? (
            <div className="tw:flex tw:items-center tw:justify-center tw:py-12">
              <div className="tw:animate-spin tw:rounded-full tw:h-8 tw:w-8 tw:border-2 tw:border-zinc-300 tw:border-t-zinc-600" />
            </div>
          ) : (
            <>
              <div
                className="tw:dark:text-white"
                dangerouslySetInnerHTML={{ __html: contractText || '' }}
              />
              <div className="tw:mt-6 tw:border-t tw:border-zinc-200 tw:pt-6">
                <p className="tw:text-stoplight-danger tw:mb-4">
                  Please enter your name to sign and accept this agreement
                </p>
                <div className="tw:grid tw:grid-cols-1 tw:sm:grid-cols-2 tw:gap-4">
                  <div>
                    <label
                      htmlFor="firstName"
                      className="tw:block tw:text-sm tw:font-medium tw:text-zinc-700 tw:mb-1"
                    >
                      First Name
                    </label>
                    <Input
                      id="firstName"
                      type="text"
                      value={signedFirstName}
                      onChange={(e) => setSignedFirstName(e.target.value)}
                      placeholder="First Name"
                      disabled={isSigningContract}
                      className="tw:w-full"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="lastName"
                      className="tw:block tw:text-sm tw:font-medium tw:text-zinc-700 tw:mb-1"
                    >
                      Last Name
                    </label>
                    <Input
                      id="lastName"
                      type="text"
                      value={signedLastName}
                      onChange={(e) => setSignedLastName(e.target.value)}
                      placeholder="Last Name"
                      disabled={isSigningContract}
                      className="tw:w-full"
                    />
                  </div>
                </div>
                <div>
                  <div className="tw:flex tw:items-center tw:justify-end tw:gap-3 tw:pt-4">
                    <Button outline onClick={handleClose} disabled={isSigningContract}>
                      Cancel
                    </Button>
                    <Button
                      color="brandgreen0"
                      onClick={handleSign}
                      disabled={
                        !signedFirstName.trim() || !signedLastName.trim() || isSigningContract
                      }
                    >
                      {isSigningContract ? (
                        <div className="tw:flex tw:items-center tw:gap-2">
                          <div className="tw:animate-spin tw:rounded-full tw:h-4 tw:w-4 tw:border-2 tw:border-white tw:border-t-transparent" />
                          <span>Signing...</span>
                        </div>
                      ) : (
                        'Sign'
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Dialog>
  );
}
